import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface SidebarProps {
    activeKey?: "dashboard" | "itaxquest" | "eci" | "formc" | "feedback";
}

export default function Sidebar({ activeKey = "dashboard" }: SidebarProps) {
    const navigate = useNavigate();
    // Open dropdown if current page is ECI or FormC
    const [openCompMenu, setOpenCompMenu] = useState(
        activeKey === "eci" || activeKey === "formc"
    );
    const menuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        function onDocClick(e: MouseEvent) {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target as Node)) {
                // Only close if it's NOT one of the active sub-pages (optional, but good UX to keep it open)
                // For now, standard dropdown behavior: click outside -> close
                // But if we are currently ON the page, maybe we want it to stay open? 
                // Let's stick to standard behavior but initialize it as open.
                setOpenCompMenu(false);
            }
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    // Helper to determine active style for main items
    const getLinkStyle = (key: string) => {
        const isActive = activeKey === key;
        if (isActive) {
            return {
                padding: "12px 16px",
                borderRadius: "8px",
                backgroundColor: "#1d2236",
                color: "#60a5fa",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                textDecoration: "none",
                fontWeight: 500,
                cursor: "pointer",
            };
        }
        return {
            padding: "12px 16px",
            borderRadius: "8px",
            color: "#9ca3af",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            textDecoration: "none",
            fontWeight: 500,
            cursor: "pointer",
            background: "transparent",
        };
    };

    return (
        <aside
            style={{
                width: "240px",
                backgroundColor: "#0f1113",
                borderRight: "1px solid #333", // Vertical separator line per design
                padding: "24px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: "100vh", // Ensure it stretches
                position: "fixed",  // Fix sidebar to left
                top: 0,
                left: 0,
                bottom: 0,
                zIndex: 50
            }}
        >
            <div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "40px",
                    }}
                >
                    {/* Logo */}
                    <img
                        src="/logo.png"
                        alt="logo"
                        style={{
                            position: "fixed",
                            top: 20,
                            left: 20,
                            width: 120,
                            height: "auto",
                            zIndex: 9999,
                        }}
                    />
                </div>

                <nav
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        fontSize: "14px",
                    }}
                >
                    {/* Dashboard */}
                    <div
                        style={getLinkStyle("dashboard")}
                        onClick={() => navigate("/preparer")}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7"></rect>
                            <rect x="14" y="3" width="7" height="7"></rect>
                            <rect x="14" y="14" width="7" height="7"></rect>
                            <rect x="3" y="14" width="7" height="7"></rect>
                        </svg>
                        Dashboard
                    </div>

                    {/* iTaxQuest */}
                    <div
                        style={getLinkStyle("itaxquest")}
                        onClick={() => navigate("/itaxquest")}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                        </svg>
                        iTaxQuest
                    </div>

                    {/* iTaxComp Dropdown */}
                    <div ref={menuRef} style={{ position: "relative" }}>
                        <button
                            onClick={() => setOpenCompMenu((v) => !v)}
                            style={{
                                padding: "12px 16px",
                                borderRadius: "8px",
                                color: (activeKey === "eci" || activeKey === "formc") ? "#e5e7eb" : "#9ca3af",
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                width: "100%",
                                background: openCompMenu ? "#111317" : "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: 500,
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                            </svg>
                            iTaxComp
                            <span style={{ marginLeft: "auto" }}>
                                {openCompMenu ? "▾" : "▸"}
                            </span>
                        </button>

                        {openCompMenu && (
                            <div
                                style={{
                                    marginTop: 4,
                                    paddingLeft: 14, // Indent for the tree line
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4
                                }}
                            >
                                <div style={{ borderLeft: "1px solid rgba(255,255,255,0.1)", paddingLeft: 12, display: "flex", flexDirection: "column", gap: 4 }}>
                                    {/* ECI Sub-item */}
                                    <div
                                        onClick={() => navigate("/eci")}
                                        style={{
                                            padding: "8px 12px",
                                            borderRadius: 6,
                                            background: activeKey === "eci" ? "rgba(59, 130, 246, 0.15)" : "transparent",
                                            color: activeKey === "eci" ? "#60a5fa" : "#9ca3af",
                                            textAlign: "left",
                                            fontWeight: activeKey === "eci" ? 600 : 500,
                                            fontSize: "13px",
                                            cursor: "pointer",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        ECI
                                    </div>

                                    {/* FormC Sub-item */}
                                    <div
                                        onClick={() => navigate("/formc")}
                                        style={{
                                            padding: "8px 12px",
                                            borderRadius: 6,
                                            background: activeKey === "formc" ? "rgba(59, 130, 246, 0.15)" : "transparent",
                                            color: activeKey === "formc" ? "#60a5fa" : "#9ca3af",
                                            textAlign: "left",
                                            fontWeight: activeKey === "formc" ? 600 : 500,
                                            fontSize: "13px",
                                            cursor: "pointer",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        Form C / C-S / C-S (Lite)
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Notifications */}
                    <div
                        style={getLinkStyle("notifications")}
                        onClick={() => {/* no-op for now */ }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                        </svg>
                        Notifications
                    </div>

                    {/* Feedback */}
                    <div
                        style={getLinkStyle("feedback")}
                        onClick={() => navigate("/feedback")}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                        </svg>
                        Feedback
                    </div>
                </nav>
            </div>

            {/* User Profile */}
            <div
                style={{
                    background: "#18181b",
                    padding: "16px",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                }}
            >
                <div
                    style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "2px solid #374151",
                    }}
                >
                    <img
                        src="https://i.pravatar.cc/150?u=preparer"
                        alt="avatar"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />
                </div>
                <div>
                    <div
                        style={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}
                    >
                        Preparer
                    </div>
                    <div
                        style={{
                            fontSize: "12px",
                            color: "#9ca3af",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            maxWidth: "140px",
                            textOverflow: "ellipsis"
                        }}
                    >
                        preparer@itax.test
                    </div>
                </div>
            </div>
        </aside>
    );
}
